import { CommonText, CommonTitle } from '@client/style/Text';
import HTML from '@client/core/utils/HTML';
import { ILocation } from './ILocation';
import Functions from '@client/style/Functions';
import { Breakpoints, Fonts } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import Image from '../Image';
import gsap, { Power0, Power1 } from 'gsap';
import useIsMobile from '@client/core/CustomHooks/useIsMobile';
import Cross2 from '@client/assets/svgs/cross-2.svg';
import AudioCircle from '@client/assets/svgs/audio-circle.svg';

const Container = styled.div<{ $bg?: string; $fg?: string }>`
	overflow-y: scroll;
	background-color: ${props => props.$bg};
	color: ${props => props.$fg};
	z-index: 1001;

	position: absolute;
	bottom: 0;
	left: 0;

	width: 100%;
	height: 100%;

	display: flex;
	flex-direction: column;

	${Functions.breakpoint(Breakpoints.tablet)} {
		width: 27%;
		min-width: 480px;
		height: 100%;
		right: 0;
		top: 0;
		left: unset;
	}
`;

const Title = styled(CommonTitle)<{ $fg?: string }>`
	padding: 0 var(--gridMargin);
	color: ${props => props.$fg};
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	margin-top: 20px;

	${Functions.breakpoint(Breakpoints.tablet)} {
		padding: 0 35px;
		margin-top: 60px;
	}
`;

const ImageWrapper = styled.div`
	display: none;
	padding: 0 35px;
	width: 100%;
	height: auto;
	aspect-ratio: 16/9;
	margin-top: 40px;

	${Functions.breakpoint(Breakpoints.tablet)} {
		display: block;
	}
`;

const InfoMid = styled.div`
	padding: 0 var(--gridMargin);

	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 20px;
	margin-bottom: 20px;
	${Functions.breakpoint(Breakpoints.tablet)} {
		margin-top: 40px;

		margin-bottom: 40px;

		padding: 0 35px;
		gap: 16px;
	}
`;

const InfoEntry = styled.div`
	display: flex;

	justify-content: space-between;
	gap: 10px;
`;

const Text = styled(CommonText)<{ $fg?: string }>`
	color: ${props => props.$fg};
	margin: 0;
	${TextStyle.size('xlarge')}

	b {
		${TextStyle.family('title')}
		${TextStyle.weight('bold')}
	}
	text-align: left;
`;
const InfoText = styled(Text)`
	flex: 0 1 50%;
`;

const InfoBottom = styled.div<{ $fg?: string; $bg?: string }>`
	color: ${props => props.$fg};
	padding: 12px var(--gridMargin);
	width: 100%;
	display: flex;
	align-items: center;
	gap: 20px;
	background-color: rgba(38, 44, 46, 0.1);

	svg #contrast {
		fill: ${props => props.$bg || 'white'};
	}
`;

type CMSColor = {
	color: string;
	contrastColor: string;
};

export default function InfoContainer({
	show,
	currentLocation,
	soundColors,
	soundTexts,
	onClose,
	onOffsetChange,
	// onOpen,
}: {
	show: boolean;
	onOffsetChange?: (offset: { x: number; y: number }) => void;
	currentLocation?: ILocation;
	soundColors: { noSoundColor: CMSColor; lowSoundColor: CMSColor; midSoundColor: CMSColor; highSoundColor: CMSColor };
	soundTexts: { lowSoundText: string; midSoundText: string; highSoundText: string };
	onClose?: () => void;
	onOpen?: () => void;
}) {
	const [renderImage, setRenderImage] = React.useState(false);
	const infoContainerRef = React.useRef<HTMLDivElement | null>(null);
	const isMobile = useIsMobile('tablet');

	const updateOffset = () => {
		if (infoContainerRef.current === null) return;
		const rect = infoContainerRef.current?.getBoundingClientRect();
		const offset = { x: rect.x - window.innerWidth, y: rect.y - window.innerHeight };
		if (isMobile) {
			offset.x = 0;
		} else {
			offset.y = 0;
		}
		if (onOffsetChange) onOffsetChange(offset);
	};

	const gsapVars = {
		mobile: {
			open: { duration: 0.125, y: 0, ease: Power0.easeIn, onUpdate: updateOffset, overwrite: true },
			closed: {
				duration: 0.125,
				y: '100%',
				onComplete: () => setRenderImage(false),
				onUpdate: updateOffset,
				overwrite: true,
			},
		},
		desktop: {
			open: { duration: 0.25, x: 0, ease: Power1.easeOut, onUpdate: updateOffset, overwrite: true },
			closed: {
				duration: 0.25,
				x: '100%',
				onComplete: () => setRenderImage(false),
				onUpdate: updateOffset,
				overwrite: true,
			},
		},
	};

	useEffect(() => {
		gsap.set(infoContainerRef.current, { clearProps: true });

		if (show) {
			gsap.set(infoContainerRef.current, isMobile ? gsapVars.mobile.open : gsapVars.desktop.open);
		} else {
			gsap.set(infoContainerRef.current, isMobile ? gsapVars.mobile.closed : gsapVars.desktop.closed);
		}
	}, [isMobile]);

	useEffect(() => {
		if (!currentLocation) return;
		if (show) {
			setRenderImage(true);

			const openTl = gsap.timeline();
			openTl.to(infoContainerRef.current, isMobile ? gsapVars.mobile.open : gsapVars.desktop.open);
		} else {
			const closeTl = gsap.timeline();
			closeTl.to(infoContainerRef.current, isMobile ? gsapVars.mobile.closed : gsapVars.desktop.closed);
		}
	}, [show]);

	const selectColor = (lydNiveauer: 'no_info' | 'low' | 'mid' | 'high' | undefined) => {
		if (lydNiveauer === 'no_info') return soundColors.noSoundColor;
		if (lydNiveauer === 'low') return soundColors.lowSoundColor;
		if (lydNiveauer === 'mid') return soundColors.midSoundColor;
		if (lydNiveauer === 'high') return soundColors.highSoundColor;
	};

	return (
		<Container
			ref={infoContainerRef}
			$bg={selectColor(currentLocation?.lydNiveauer)?.color}
			$fg={selectColor(currentLocation?.lydNiveauer)?.contrastColor}
		>
			<Title $fg={selectColor(currentLocation?.lydNiveauer)?.contrastColor}>
				{currentLocation?.title}
				<Cross2 onClick={() => onClose && onClose()} />
			</Title>
			{renderImage && (
				<ImageWrapper>
					<Image data={currentLocation?.image}></Image>
				</ImageWrapper>
			)}
			<InfoMid>
				{currentLocation?.info.map((info, index) => {
					return (
						<InfoEntry key={'MapInfoEntry_' + index}>
							<InfoText
								style={{ fontFamily: Fonts.families.title, fontWeight: Fonts.weights.bold }}
								$fg={selectColor(currentLocation?.lydNiveauer)?.contrastColor}
							>
								{info.label}
							</InfoText>
							<InfoText style={{ textAlign: 'right' }} $fg={selectColor(currentLocation?.lydNiveauer)?.contrastColor}>
								{info.text}
							</InfoText>
						</InfoEntry>
					);
				})}
			</InfoMid>
			{currentLocation?.lydNiveauer !== 'no_info' && (
				<InfoBottom
					$fg={selectColor(currentLocation?.lydNiveauer)?.contrastColor}
					$bg={selectColor(currentLocation?.lydNiveauer)?.color}
				>
					<AudioCircle />
					{currentLocation?.lydNiveauer === 'low' ? (
						<Text dangerouslySetInnerHTML={{ __html: HTML.clean(soundTexts.lowSoundText) }}></Text>
					) : currentLocation?.lydNiveauer === 'mid' ? (
						<Text dangerouslySetInnerHTML={{ __html: HTML.clean(soundTexts.midSoundText) }}></Text>
					) : currentLocation?.lydNiveauer === 'high' ? (
						<Text dangerouslySetInnerHTML={{ __html: HTML.clean(soundTexts.highSoundText) }}></Text>
					) : (
						''
					)}
				</InfoBottom>
			)}
		</Container>
	);
}
